// Shared threshold extraction + overbought/oversold band-clip geometry for
// the oscillator series (RSI, CCI, UltimateOscillator, SchaffTrendCycle,
// RelativeVolatility). Extracted from 24 inline copies (dedup audit
// 2026-07-17, theme 2) — a NaN-guard/clamp fix here reaches all five.

/**
 * Extract the numeric value from a ThresholdLineType prop.
 * Accepts: number | {value} | single-key form object | {defaultValue}.
 */
export function extractThresholdValue(prop, defaultValue) {
  if (typeof prop === "number") return prop;
  if (prop && typeof prop === "object") {
    if (prop.value !== undefined) return prop.value;
    const keys = Object.keys(prop);
    if (keys.length === 1) return prop[keys[0]];
    if (prop.defaultValue !== undefined) return prop.defaultValue;
  }
  return defaultValue;
}

/**
 * Extract {value, stroke} from a ThresholdLineType prop.
 * Accepts: number (backwards compat) | {value, stroke} | single-key form
 * object (legacy) | {value} | {defaultValue}.
 */
export function extractThresholdLine(prop, defaultValue, defaultStroke) {
  if (typeof prop === "number") {
    return { value: prop, stroke: defaultStroke };
  }
  if (prop && typeof prop === "object") {
    if (prop.value !== undefined && prop.stroke) {
      return { value: prop.value, stroke: prop.stroke };
    }
    const keys = Object.keys(prop);
    if (keys.length === 1) {
      return { value: prop[keys[0]], stroke: defaultStroke };
    }
    if (prop.value !== undefined) {
      return { value: prop.value, stroke: defaultStroke };
    }
    if (prop.defaultValue !== undefined) {
      return { value: prop.defaultValue, stroke: defaultStroke };
    }
  }
  return { value: defaultValue, stroke: defaultStroke };
}

/** Canvas clip: inside the band (between overbought and oversold). */
export function clipToBand(ctx, moreProps, overBoughtValue, overSoldValue) {
  const { yScale, width } = moreProps.chartConfig;
  // In Y scale, higher values (overbought) have lower Y coordinates
  const overBoughtY = yScale(overBoughtValue);
  const overSoldY = yScale(overSoldValue);

  ctx.beginPath();
  ctx.rect(0, overBoughtY, width, overSoldY - overBoughtY);
  ctx.clip();
}

/** Canvas clip: outside the band (above overbought + below oversold). */
export function clipOutsideBand(
  ctx,
  moreProps,
  overBoughtValue,
  overSoldValue,
) {
  const { yScale, width, height } = moreProps.chartConfig;
  const overBoughtY = yScale(overBoughtValue);
  const overSoldY = yScale(overSoldValue);

  ctx.beginPath();
  ctx.rect(0, 0, width, overBoughtY);
  ctx.rect(0, overSoldY, width, height - overSoldY);
  ctx.clip();
}

/** Canvas clip: above the overbought line only. */
export function clipAboveOverBought(ctx, moreProps, overBoughtValue) {
  const { yScale, width } = moreProps.chartConfig;

  ctx.beginPath();
  ctx.rect(0, 0, width, yScale(overBoughtValue));
  ctx.clip();
}

/** Canvas clip: below the oversold line only. */
export function clipBelowOverSold(ctx, moreProps, overSoldValue) {
  const { yScale, width, height } = moreProps.chartConfig;
  const overSoldY = yScale(overSoldValue);

  ctx.beginPath();
  ctx.rect(0, overSoldY, width, height - overSoldY);
  ctx.clip();
}

/** Band geometry for the SVG <clipPath> defs in renderClip. */
export function bandClipRects(moreProps, overBoughtValue, overSoldValue) {
  const { yScale, width, height } = moreProps.chartConfig;
  return {
    overBoughtY: yScale(overBoughtValue),
    overSoldY: yScale(overSoldValue),
    width,
    height,
  };
}
